import getLogger from "@/lib/logger";
import {
  PORTAL_OTP_PHONE_MULTIPLE_ACCOUNTS_MESSAGE,
  portalOtpEmailMultipleAccountsMessage,
} from "@/lib/modules/auth/utils";
import {
  maskEmailForLog,
  maskPhoneNumberForLog,
} from "@/lib/modules/auth/log-redaction";

const logger = getLogger("Portal:OtpLoginForm");

export {
  PORTAL_OTP_PHONE_MULTIPLE_ACCOUNTS_MESSAGE,
  portalOtpEmailMultipleAccountsMessage,
};

export type PortalOtpAuthMethod = "sms" | "email";

export type PortalOtpLogContact = {
  phone?: string;
  phoneNumber?: string;
  email?: string;
  contactInfo?: string;
};

export const toPortalOtpLogMethod = (
  authMethod: "phone" | "email",
): PortalOtpAuthMethod => {
  return authMethod === "phone" ? "sms" : "email";
};

export const logPortalOtpWarn = (
  message: string,
  status: string,
  method: PortalOtpAuthMethod,
  contact: PortalOtpLogContact,
  extra?: Record<string, unknown>,
) => {
  logger.warn(
    {
      action: "portal_login_attempt",
      method,
      status,
      ..._maskContact(contact),
      ...extra,
    },
    message,
  );
};

export const logPortalOtpError = (
  message: string,
  method: PortalOtpAuthMethod,
  contact: PortalOtpLogContact,
  error: unknown,
  extra?: Record<string, unknown>,
) => {
  logger.error(
    {
      action: "portal_login_attempt",
      method,
      status: "error",
      ..._maskContact(contact),
      error: error instanceof Error ? error.message : String(error),
      ...extra,
    },
    message,
  );
};

export const logPortalOtpInfo = (
  message: string,
  method: PortalOtpAuthMethod,
  contact: PortalOtpLogContact,
  extra?: Record<string, unknown>,
) => {
  logger.info(
    {
      action: "portal_login_attempt",
      method,
      ..._maskContact(contact),
      ...extra,
    },
    message,
  );
};

type SwitchToEmailAfterPhoneMultipleAccountsArgs = {
  setMultipleAccountsError: (message: string | null) => void;
  setAuthMethod: (method: "phone" | "email") => void;
  setStep: (step: "phone" | "email" | "otp") => void;
  setContactInfo: (value: string) => void;
  resetContactForms: () => void;
  method: PortalOtpAuthMethod;
  contact: PortalOtpLogContact;
  logMessage: string;
};

export const switchToEmailAfterPhoneMultipleAccounts = ({
  setMultipleAccountsError,
  setAuthMethod,
  setStep,
  setContactInfo,
  resetContactForms,
  method,
  contact,
  logMessage,
}: SwitchToEmailAfterPhoneMultipleAccountsArgs) => {
  logPortalOtpWarn(logMessage, "multiple_accounts_detected", method, contact);
  setMultipleAccountsError(PORTAL_OTP_PHONE_MULTIPLE_ACCOUNTS_MESSAGE);
  setAuthMethod("email");
  setStep("email");
  resetContactForms();
  setContactInfo("");
};

type ShowEmailMultipleAccountsBannerArgs = {
  setMultipleAccountsError: (message: string | null) => void;
  method: PortalOtpAuthMethod;
  contact: PortalOtpLogContact;
  logMessage: string;
  serverError?: string | null;
};

export const showEmailMultipleAccountsBanner = ({
  setMultipleAccountsError,
  method,
  contact,
  logMessage,
  serverError,
}: ShowEmailMultipleAccountsBannerArgs) => {
  logPortalOtpWarn(
    logMessage,
    "multiple_accounts_detected",
    method,
    contact,
    serverError === undefined ? undefined : { error: serverError },
  );
  setMultipleAccountsError(portalOtpEmailMultipleAccountsMessage());
};

type SetOtpUserFacingErrorArgs = {
  setError: (message: string | null) => void;
  method: PortalOtpAuthMethod;
  contact: PortalOtpLogContact;
  logMessage: string;
  status: string;
  serverError?: string | null;
  fallbackMessage: string;
};

export const setOtpUserFacingError = ({
  setError,
  method,
  contact,
  logMessage,
  status,
  serverError,
  fallbackMessage,
}: SetOtpUserFacingErrorArgs) => {
  logPortalOtpWarn(logMessage, status, method, contact, {
    error: serverError,
  });
  setError(serverError || fallbackMessage);
};

export const setUnexpectedOtpError = (
  error: unknown,
  method: PortalOtpAuthMethod,
  contact: PortalOtpLogContact,
  logMessage: string,
  setError: (message: string | null) => void,
  extra?: Record<string, unknown>,
) => {
  logPortalOtpError(logMessage, method, contact, error, extra);
  setError("An unexpected error occurred. Please try again.");
};

export type PortalOtpActionFailure = {
  error?: string | null;
  errorCode?: string | null;
};

type HandlePortalOtpActionFailureArgs = {
  result: PortalOtpActionFailure;
  setError: (message: string | null) => void;
  setMultipleAccountsError: (message: string | null) => void;
  switchToEmailDeps: Pick<
    SwitchToEmailAfterPhoneMultipleAccountsArgs,
    "setAuthMethod" | "setStep" | "setContactInfo" | "resetContactForms"
  >;
  switchToEmailLogMessage: string;
  switchToEmailMethod: PortalOtpAuthMethod;
  switchToEmailContact: PortalOtpLogContact;
  emailMultipleLogMessage: string;
  emailMultipleMethod: PortalOtpAuthMethod;
  emailMultipleContact: PortalOtpLogContact;
  genericFailure: Omit<SetOtpUserFacingErrorArgs, "setError" | "serverError">;
};

/**
 * Maps send / verify / resend failures to UI state and logging.
 * Handles multiple-account cases and falls back to a generic user-facing error.
 */
export const handlePortalOtpActionFailure = ({
  result,
  setError,
  setMultipleAccountsError,
  switchToEmailDeps,
  switchToEmailLogMessage,
  switchToEmailMethod,
  switchToEmailContact,
  emailMultipleLogMessage,
  emailMultipleMethod,
  emailMultipleContact,
  genericFailure,
}: HandlePortalOtpActionFailureArgs) => {
  if (result.errorCode === "MULTIPLE_ACCOUNTS_PHONE") {
    switchToEmailAfterPhoneMultipleAccounts({
      setMultipleAccountsError,
      ...switchToEmailDeps,
      method: switchToEmailMethod,
      contact: switchToEmailContact,
      logMessage: switchToEmailLogMessage,
    });
    return;
  }

  if (result.errorCode === "MULTIPLE_ACCOUNTS_EMAIL") {
    showEmailMultipleAccountsBanner({
      setMultipleAccountsError,
      method: emailMultipleMethod,
      contact: emailMultipleContact,
      logMessage: emailMultipleLogMessage,
      serverError: result.error,
    });
    return;
  }

  setOtpUserFacingError({
    setError,
    serverError: result.error,
    ...genericFailure,
  });
};

const _maskContact = ({
  phone,
  phoneNumber,
  email,
  contactInfo,
}: PortalOtpLogContact) => ({
  phone: maskPhoneNumberForLog(phone),
  phoneNumber: maskPhoneNumberForLog(phoneNumber),
  email: maskEmailForLog(email),
  contactInfo: contactInfo?.includes("@")
    ? maskEmailForLog(contactInfo)
    : maskPhoneNumberForLog(contactInfo),
});
